import {
  type QueryKeyUnion,
  useGqtyClient,
  useInvalidateHasuraQueries,
  useTrpcMutation,
  useTeam,
} from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import {
  parseAsString,
  parseAsArrayOf,
  useQueryState,
  type HistoryOptions,
} from '@vrplatform/query-params';
import { day, ensure, hasValue, waitFor, waitUntil } from '@vrplatform/utils';
import {
  getSourceNamespaceAndType,
  getSyncStatus,
} from '@vrplatform/ui-common';
import { useMemo } from 'react';
import { useAutomationsQuery } from '@vrplatform/data';

export const runParamKey = 'runAutomationId'; // string
export const resultParamKey = 'workflowIds'; // string[]
export const resultViewParamKey = 'runResultView'; // string

type DrawerType = 'run' | 'workflows';

type RunInput = {
  automationId: string;
  params: any;
};

export const useAutomationHistoryDrawer = (onClose?: () => void) => {
  const { mutate, loading: runTaskLoading } = useRunMutation();

  const { data: automations = [] } = useAutomationsQuery();

  const [automationId, setAutomationId] = useQueryState(
    runParamKey,
    parseAsString
  );
  const [syncIds, setWorkflowIds] = useQueryState(
    resultParamKey,
    parseAsArrayOf(parseAsString)
  );
  const [, setResultView] = useQueryState(resultViewParamKey, parseAsString);
  const [refreshKeys, setRefreshKeys] = useQueryState(
    'invalidate',
    parseAsArrayOf(parseAsString)
  );

  const invalidate = useInvalidateHasuraQueries();

  const activeDrawer = useMemo<DrawerType | undefined>(() => {
    if (syncIds?.length) return 'workflows';
    if (automationId) return 'run';
    return undefined;
  }, [automationId, syncIds]);

  const client = useGqtyClient();

  const closeDrawer = (drawer?: DrawerType) => {
    if (drawer === 'run') return setAutomationId(null);
    if (drawer === 'workflows') {
      setResultView(null);
      return setWorkflowIds(null);
    }

    setAutomationId(null, { history: 'replace' });
    setResultView(null, { history: 'replace' });
    setRefreshKeys(null, { history: 'replace' });
    setWorkflowIds(null, { history: 'push' });

    if (onClose) onClose();
  };

  const awaitRefresh = (ids: string[], queryKeys: QueryKeyUnion[]) =>
    waitUntil(
      async () =>
        await client.query((q) => {
          return q
            .syncs({
              where: {
                id: { _in: ids },
              },
              order_by: [{ createdAt: 'desc_nulls_last' }],
            })
            .map((sync) => getSyncStatus(sync));
        }),
      (workflows) => {
        return (
          !!workflows.length &&
          workflows.every((s) =>
            ensure<(typeof s)[]>(['completed', 'canceled', 'failed']).includes(
              s
            )
          )
        );
      },
      { retryEvery: '2s', timeoutAfter: '3m' }
    ).then(() => {
      invalidate(queryKeys);
    });

  const setSyncs = (
    syncIds: string[] | string,
    options?: {
      type?: HistoryOptions;
      refreshQueryKeys?: QueryKeyUnion[];
    }
  ) => {
    const ids = Array.isArray(syncIds) ? syncIds : [syncIds];
    setWorkflowIds(ids, { history: options?.type });
    setResultView('workflows');

    if (options?.refreshQueryKeys) {
      setRefreshKeys(options.refreshQueryKeys);
      awaitRefresh(ids, options.refreshQueryKeys);
    }
  };

  const runTask = async (
    args: RunInput,
    options?: Parameters<typeof setSyncs>[1] & {
      preventRunDrawer?: boolean;
    }
  ) => {
    const result = await mutate(args);
    if (!options?.preventRunDrawer) setSyncs(result.workflowId, options);
    return result;
  };

  return {
    activeDrawer,
    close: closeDrawer,
    setAutomationId,
    automationId,
    syncIds,
    refreshKeys,
    open: setSyncs,
    runTask,
    runTaskLoading,
    postAutomations: async ({
      automationIds,
      allPagesSelected,
      filterState,
      getSelectedRowIds,
      where,
      skipCachedActions,
      selectedItemType,
      extraParams,
      skipSelectionNeccesary,
    }: {
      automationIds: string[];
      getSelectedRowIds: () => string[];
      allPagesSelected: boolean;
      filterState: Record<string, any>;
      where: Record<string, any>;
      skipCachedActions?: boolean;
      skipSelectionNeccesary: boolean;
      selectedItemType: string;
      extraParams?: Record<string, any>;
    }) => {
      const ids = allPagesSelected ? [] : getSelectedRowIds();

      const missingName = selectedItemType.split('.')[1];

      if (!skipSelectionNeccesary && !ids?.length && !allPagesSelected) {
        showWarnNotification({
          title: `Missing ${missingName}`,
          message: `Please select at least one ${missingName} to post.`,
        });

        return Promise.reject();
      }

      const filter: Record<string, any> = {};
      Object.entries(filterState || {}).forEach(([key, value]) => {
        const isEmptyObject =
          typeof value === 'object' &&
          !Array.isArray(value) &&
          Object.keys(value ?? {}).length === 0;

        const isEmptyArray = Array.isArray(value) && value.length === 0;

        if (value && !isEmptyObject && !isEmptyArray) filter[key] = value;
      });

      // Prevent sending all pages when not filtered
      if (
        !skipSelectionNeccesary &&
        allPagesSelected &&
        Object.keys(filter).length < 1
      ) {
        showWarnNotification({
          title: 'Filter missing',
          message: 'Please set at least one filter when selecting all pages.',
          color: 'yellow',
        });

        return Promise.reject();
      }

      const result: Awaited<ReturnType<typeof runTask>>[] = [];

      // we need to run in sequence and add a waitFor until we moved to inngest, else we risk duplicated data since backend concurrency is not working well
      for (const automationId of automationIds) {
        if (automationId !== automationIds[0]) await waitFor(250);
        const automation = automations.find(
          (i) => i.automationId === automationId
        );
        const input = getAutomationInput(automation?.template);

        const getDate = () => {
          const dateRange: [Date | null, Date | null] | undefined | string =
            filter?.date;

          if (dateRange && typeof dateRange === 'string') return dateRange;

          if (Array.isArray(dateRange))
            return dateRange
              .filter(hasValue)
              .map((i) => day(i).format('YYYY-MM-DD'))
              .join('...');

          return undefined;
        };

        const res = await runTask(
          {
            automationId,
            //input: ids.length === 0 || allPagesSelected ? undefined : ids,
            params: {
              skipCachedActions,
              ...extraParams,
              // Send the filters only when all pages are selected
              ...(allPagesSelected
                ? { ...filter, date: getDate(), where }
                : {}),
              [input.field]:
                ids.length === 0 || allPagesSelected ? undefined : ids,
            },
          },
          {
            preventRunDrawer: true,
          }
        );
        result.push(res);
      }

      const syncs = result.map((x) => x.syncId).filter(hasValue);

      setSyncs(syncs);

      return result;
    },
  };
};

const useRunMutation = () => {
  const [{ id: teamId }] = useTeam();
  const { mutate, loading } = useTrpcMutation('runAutomation', {
    successMessage: {
      title: 'Automation Progress',
      message:
        "Automation is running. Follow the progress and each step's result.",
    },
    invalidateQueryKeys: ['automations'],
  });

  const runAutomation = async (args: RunInput) => {
    return await mutate({ ...args, teamId });
  };

  return {
    mutate: runAutomation,
    loading,
  };
};

export const getAutomationInput = (
  template:
    | {
        // title: string | undefined;
        // uniqueRef: string | undefined;
        input: string | undefined;
        params: any;
      }
    | undefined
): {
  field: string;
  namespace: string;
  type: string;
  schema: string;
} => {
  if (!template) return { field: '', namespace: '', type: '', schema: '' };
  if (template.input && !['form', 'report'].includes(template.input)) {
    const [namespace, type] = getSourceNamespaceAndType(template.input);
    return {
      field: 'input',
      namespace,
      type,
      schema: `${namespace}.${type}`,
    };
  }
  const d = Object.entries(template.params?.properties || {})
    .filter(
      ([key, value]: [string, any]) =>
        value.schema &&
        (key.endsWith('Id') || key.endsWith('No') || key === 'input')
    )
    .map(([field, value]: [string, any]) => {
      const [namespace, type] = getSourceNamespaceAndType(value.schema);
      return { field, namespace, type, schema: `${namespace}.${type}` };
    })[0];
  if (d) return d;
  return { field: '', namespace: '', type: '', schema: '' };
};
